import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import './RetailerUrlFormatting.css';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { retailerUrlFormats, RetailerUrlFormat } from './retailerUrlFormats';
import PageHeader from '../../components/PageHeader/PageHeader';
import { selectCurrentOrgId } from '../../reducers/profile/profileSlice';
import { fetchRetailers } from '../../controllers/product-service';
import { fetchOrganizationSubscriptions } from '../../controllers/subscription-service';
import { AsyncState } from '../../utils/webRequests.type';
import { RetailerResponse } from '../../controllers/subscription-service/types';
import sendRumError from '../../utils/datadogRum';
import { HelpLinkButton } from '../../components/modal/helpModal/helpModal';
import LoadingPage from '../LoadingPage/LoadingPage';

/* istanbul ignore next */
const validateRetailerUrl = (
  regex: string[] | undefined,
  sample: string,
): boolean => {
  if (!regex) return false;

  return regex.some((pattern) => {
    try {
      return new RegExp(pattern).test(sample);
    } catch (err) {
      return false;
    }
  });
};

type RetailerValidProductUrlResponse = {
  id: string,
  name: string,
  homepage: string,
  valid_product_url_patterns: string[],
};

/* istanbul ignore next */
const handleTestUrl = (e:React.MouseEvent | React.KeyboardEvent, { regex }:RetailerUrlFormat) => {
  e.preventDefault();
  e.stopPropagation();
  const input = e.currentTarget.parentElement?.querySelector('.retailer-url-input-validator') as HTMLInputElement;
  if (!input) return;
  const { value } = input;
  const isValid = validateRetailerUrl(regex, value);
  input.classList.remove('is-valid', 'is-invalid');
  input.classList.add(isValid ? 'is-valid' : 'is-invalid');
};

export default function RetailerUrlFormatting() {
  const currentOrgId = useSelector(selectCurrentOrgId);
  const [selectedRetailers, setSelectedRetailers] = useState<RetailerUrlFormat[]>([]);
  const [subscribedRetailersFetchState, setSubscribedRetailersFetchState] = useState<AsyncState>('uninitialized');
  const [subscribedRetailers, setSubscribedRetailers] = useState<RetailerResponse[]>([]);
  const [productServiceRetailersFetchState, setProductServiceRetailersFetchState] = useState<AsyncState>('uninitialized');
  // eslint-disable-next-line max-len
  const [productServiceRetailers, setProductServiceRetailers] = useState<RetailerValidProductUrlResponse[]>([]);

  const loadSubscribedRetailers = async () => {
    // exit if invalid org (don't even try)
    if (Number(currentOrgId) < 1) { return; }

    setSubscribedRetailersFetchState('loading');
    // Loads a list of all retailers this organization subscribes to.
    fetchOrganizationSubscriptions(
      Number(currentOrgId),
      [],
    )
      .then(async (response) => {
        const data = response.data.map((d) => (
          { id: d.retailer_id, name: d.retailer_name }
        ));
        setSubscribedRetailers(data);
        setSubscribedRetailersFetchState('completed');
      })
      .catch((error) => {
        sendRumError(error);
        setSubscribedRetailersFetchState('failed');
      });
  };

  // Loads retailer product_url formatting information
  const loadProductServiceRetailers = async () => {
    // exit if invalid org (don't even try)
    if (Number(currentOrgId) < 1) { return; }

    setProductServiceRetailersFetchState('loading');
    fetchRetailers(Number(currentOrgId))
      .then(async (response) => {
        const data = response.data.map((d) => (
          {
            id: d.id,
            name: d.name,
            homepage: d.homepage,
            valid_product_url_patterns: d.valid_product_url_patterns,
          }
        ));
        setProductServiceRetailers(data);
        setProductServiceRetailersFetchState('completed');
      })
      .catch((error) => {
        sendRumError(error);
        setProductServiceRetailersFetchState('failed');
      });
  };

  useEffect(() => {
    loadSubscribedRetailers();
    loadProductServiceRetailers();
  }, [currentOrgId]);

  const isSubscribedRetailer = (retailer: RetailerUrlFormat) => {
    const rv = subscribedRetailers.some((ele) => ele.name === retailer.retailer_db_name);
    return rv;
  };

  useEffect(() => {
    setSelectedRetailers(retailerUrlFormats.filter(isSubscribedRetailer));
  }, [subscribedRetailersFetchState]);

  useEffect(() => {
    if (productServiceRetailersFetchState === 'completed' && subscribedRetailersFetchState === 'completed') {
      setSelectedRetailers((prev) => prev.map((retailer) => {
        const match = productServiceRetailers.find((r) => r.name === retailer.retailer_db_name);
        return match ? { ...retailer, regex: match.valid_product_url_patterns } : retailer;
      }));
    }
  }, [productServiceRetailersFetchState, subscribedRetailersFetchState, productServiceRetailers]);

  // run after data load: scroll to element identified hash
  useEffect(() => {
    const hashArray = window.location.hash.split('#') ?? [];

    if (hashArray.length === 2) {
      const targetElement = document.getElementById(hashArray[1]);
      if (targetElement) {
        targetElement.scrollIntoView();
      }
    }
  }, [selectedRetailers]);

  const bothCompleted = subscribedRetailersFetchState === 'completed'
    && productServiceRetailersFetchState === 'completed';

  if (!bothCompleted) { return <LoadingPage displayText="Loading Retailer Formats" displayEllipsis />; }

  return (
    <div className="page retailer-url-formatting-page h-100 col-12">
      <PageHeader title="Accepted Retailer Product URL Formats" returnText="Back to Home" returnLink="/" />
      <section id="intro">
        <p>
          If the retailer you are looking for is not listed, please reach out to
          <HelpLinkButton helpText="help" />
          .
        </p>
      </section>
      {
        selectedRetailers.map(
          (retailer, index) => (
            <div
              className="PageContent row"
              key={`${retailer.retailer_home}-${index}`}
              id={retailer.retailer_home}
            >
              <h3>{retailer.retailer_name}</h3>
              {
                retailer.requirements.length === 1 && (
                  <b>Requirement:</b>
                )
              }
              {
                retailer.requirements.length > 1 && (
                  <b>Requirements:</b>
                )
              }
              <ol>
                {
                  retailer.requirements.map(
                    (requirement) => requirement,
                  )
                }
              </ol>

              {
                retailer.examples.length === 1 && (
                  <b>Example:</b>
                )
              }
              {
                retailer.examples.length > 1 && (
                  <b>Examples:</b>
                )
              }
              <ol>
                {
                  retailer.examples.map(
                    (example) => example,
                  )
                }
              </ol>
              {retailer.regex && (
                <>
                  <InputGroup className="w-75 d-flex ms-4">
                    <Form.Control
                      type="url"
                      placeholder="Enter URL to test"
                      className="retailer-url-input-validator"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleTestUrl(e, retailer);
                        }
                      }}
                      onChange={(e) => { e.currentTarget.classList.remove('is-valid', 'is-invalid'); }}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={(e) => handleTestUrl(e, retailer)}
                    >
                      Test URL
                    </Button>
                  </InputGroup>
                  <small className="text-a8-grey ms-4 ps-6 pb-1">* A URL with a valid format may still link to a discontinued or suppressed product page.</small>
                </>
              )}
            </div>
          ),
        )
      }
    </div>
  );
}
